import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Loader2, CheckCircle2, HelpCircle, AlertTriangle,
  FileSearch, MessageCircleQuestion, Info, FileText, Download, Sparkles,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

export default function ReportView() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);

  useEffect(() => {
    api.get(`/reports/${reportId}`)
      .then(({ data }) => setReport(data))
      .catch(() => setError(true));
    // Use the CURRENT authenticated user's subscription status (never hard-coded).
    api.get("/stats")
      .then(({ data }) => setIsSubscriber(!!data.access?.subscription_active))
      .catch(() => setIsSubscriber(false));
  }, [reportId]);

  if (error) {
    return (
      <DashboardLayout title="Report not found">
        <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
          <Link to="/reports"><ArrowLeft className="h-4 w-4 mr-2" />Back to reports</Link>
        </Button>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout title="Loading report…">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
        </div>
      </DashboardLayout>
    );
  }

  const r = report.result;
  const metrics = [
    { label: "Vagueness Detected", value: r.metrics.vagueness },
    { label: "Emotional State", value: r.metrics.emotional_state },
    { label: "Repetition Index", value: r.metrics.repetition_index },
    { label: "Communication Signal", value: r.metrics.communication_signal },
  ];

  return (
    <DashboardLayout
      title="Communication Report"
      subtitle={report.filename}
      action={
        <div className="flex items-center gap-3">
          {isSubscriber ? (
            <Button
              disabled
              aria-disabled="true"
              data-testid="download-pdf-btn"
              className="bg-cyan-400/90 text-black font-semibold disabled:opacity-100 cursor-not-allowed"
              title="PDF download coming soon"
            >
              <Download className="h-4 w-4 mr-2" />PDF download coming soon
            </Button>
          ) : (
            <Button
              asChild
              data-testid="upgrade-pdf-btn"
              variant="outline"
              className="border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200"
            >
              <Link to="/billing"><Sparkles className="h-4 w-4 mr-2" />Subscribe to download reports</Link>
            </Button>
          )}
          <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
            <Link to="/reports" data-testid="back-to-reports"><ArrowLeft className="h-4 w-4 mr-2" />All reports</Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6" data-testid="report-view">
        {/* Header card */}
        <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-6 sm:p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-mono text-xs text-slate-500">glexis · {report.report_id}</p>
              <p className="mt-1 text-sm text-slate-400">{formatDate(report.created_at)}</p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Overall Confidence</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-4xl font-bold text-cyan-400 gx-text-glow" data-testid="report-confidence-score">
                    {r.overall_confidence}%
                  </span>
                  <span className="text-sm text-slate-400">{r.confidence_level}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="mt-6">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Confidence Over Time</p>
            <div className="h-48 w-full" style={{ minHeight: 180 }}>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={r.confidence_over_time} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cyanFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="t" stroke="#64748B" fontSize={11} tickLine={false} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ background: "#0A1124", border: "1px solid #1E293B", borderRadius: 8, color: "#fff" }}
                    labelStyle={{ color: "#94A3B8" }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#00E5FF" strokeWidth={2} fill="url(#cyanFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Metrics */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {metrics.map((m) => (
              <div key={m.label} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid={`metric-${m.label}`}>
                <p className="text-xs text-slate-500">{m.label}</p>
                <p className="mt-1 font-mono text-lg font-semibold text-white">{m.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* AI Summary */}
        <Section icon={Info} title="Summary" tone="cyan">
          <p className="text-slate-300 leading-relaxed">{r.ai_summary}</p>
        </Section>

        <div className="grid gap-6 lg:grid-cols-2">
          <ListSection icon={FileSearch} title="Communication Patterns" items={r.communication_patterns} tone="blue" />
          <ListSection icon={CheckCircle2} title="Areas of Clarity" items={r.areas_of_clarity} tone="green" />
          <ListSection icon={HelpCircle} title="Areas of Ambiguity" items={r.areas_of_ambiguity} tone="amber" />
          <ListSection icon={AlertTriangle} title="Missing Information" items={r.missing_information} tone="amber" />
        </div>

        <ListSection icon={MessageCircleQuestion} title="Suggested Follow-Up Questions" items={r.suggested_follow_up_questions} tone="cyan" numbered />

        {/* Transcript */}
        <Section icon={FileText} title="Transcript" tone="slate">
          <pre className="whitespace-pre-wrap font-mono text-sm text-slate-300 leading-relaxed">{r.transcript}</pre>
        </Section>

        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-500">
          {r.disclaimer}
        </div>
      </div>
    </DashboardLayout>
  );
}

const TONES = {
  cyan: "text-cyan-400 border-cyan-400/40 bg-cyan-400/10",
  blue: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  green: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  amber: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  slate: "text-slate-400 border-slate-700 bg-slate-800",
};

function Section({ icon: Icon, title, children, tone }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg border ${TONES[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ListSection({ icon, title, items, tone, numbered }) {
  return (
    <Section icon={icon} title={title} tone={tone}>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3 text-slate-300">
            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${numbered ? "hidden" : ""}`}
                  style={{ background: "#00E5FF" }} />
            {numbered && (
              <span className="font-mono text-xs text-cyan-400 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
            )}
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
